import { css, Interpolation, Theme } from '@emotion/react';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import dayjs from 'dayjs';
import { ColumnHeaderInfoTip } from '@frontend/analytics-shared';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { AiOfficeUser, WithTooltip } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { callIntelAiUtils, callIntelUtils, DEFAULT_FIELD_EMPTY_VALUE } from '../../utils';
import { AiOutcomeIcon } from '../ai-outcome-icon';
import { EmptyCell } from '../empty-cell';
import { CallIntelTooltip } from '../tooltip';
import { OfficeUserNameOld } from '../user-name';

type ColumnCellProps = {
  callInsights: Call;
};

export const LocationColumn = {
  Header: () => {
    const { t } = useTranslation('call-intel-core');
    return t('Location');
  },
  Cell: ({
    callInsights,
    locationName,
    maxWidth,
    noTruncating,
  }: ColumnCellProps & { locationName?: string; maxWidth?: number; noTruncating?: boolean }) => {
    const { locationId } = callInsights;
    if (!locationId) return <EmptyCell showTooltip={false} />;

    return locationName ? (
      <Chip.Location maxWidth={noTruncating ? 'none' : maxWidth ? `${maxWidth}px` : '120px'} variant='neutral'>
        {locationName}
      </Chip.Location>
    ) : null;
  },
};

export const TimeColumn = {
  Header: () => i18next.t('Time', { ns: 'call-intel-core' }),
  accessor: (startTime: string) => dayjs(startTime).format('MMM D, YYYY, h:mm A'),
};

export const OfficeNumberColumn = {
  Header: () => i18next.t('Office Number', { ns: 'call-intel-core' }),
  accessor: ({ callInsights }: ColumnCellProps) => {
    const { officeNumber, startTime } = callInsights;
    return callIntelUtils.isOfficeUserDataUnavailable(startTime)
      ? i18next.t('Unavailable Data', { ns: 'call-intel-core' })
      : officeNumber?.nationalNumber
      ? formatPhoneNumber(`${officeNumber.nationalNumber}`)
      : DEFAULT_FIELD_EMPTY_VALUE;
  },
};

export const AiOutcomeColumn = {
  Header: () => (
    <ColumnHeaderInfoTip
      infoTip={<CallIntelTooltip tip='aiCallOutcome' />}
      leftElement={
        <WithTooltip tooltipText={i18next.t('AI Call Outcome', { ns: 'call-intel-core' })}>
          <Icon name='aiweave-assistant' size={16} />
        </WithTooltip>
      }
    />
  ),
  Cell: ({ callInsights, customStyles }: ColumnCellProps & { customStyles?: Interpolation<Theme> }) => {
    const { aiOutcome } = callInsights;

    if (!callIntelAiUtils.isValidAiOutcome(aiOutcome)) return null;

    return (
      <div css={[styles.centerAlignedCell, customStyles]}>
        <AiOutcomeIcon outcome={aiOutcome} showTooltip />
      </div>
    );
  },
  HeaderLabel: () => (
    <div css={styles.headerLabelWithWrapper}>
      <Icon name='aiweave-assistant' size={16} /> {i18next.t('AI Call Outcome', { ns: 'call-intel-core' })}
    </div>
  ),
};

export const OfficeUserColumn = {
  Header: () => i18next.t('Office User', { ns: 'call-intel-core' }),
  Cell: ({ callInsights }: ColumnCellProps) => {
    const { officeUser, startTime } = callInsights;
    const isAiOfficeUser = callIntelAiUtils.isAiOfficeUser(officeUser);

    if (isAiOfficeUser) {
      return <AiOfficeUser />;
    }

    return (
      <OfficeUserNameOld
        {...officeUser}
        isDataUnavailable={callIntelUtils.isOfficeUserDataUnavailable(startTime)}
        isInactive={false}
      />
    );
  },
};

const styles = {
  headerLabelWithWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  centerAlignedCell: css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  `,
};
